import {Container, Singleton} from 'typescript-ioc'
import BaseApi from '@/api/BaseApi'
import ThingModel, {Property, Resource, ThingModelAware, Tsl} from "@/model/ThingModel";
import {ResourceDeleteForm, ResourcesUpsertForm} from "@/form/TmodelForm";
import ParamProxy, {ProxyMode} from '@/module/thing/ParamProxy';
import ArrayUtil from "tools-ts/src/util/ArrayUtil";
import {cannotFindPropErr} from "@/module/thing/TmodelError";
import ierror from "tools-ts/src/error/InnerError";
import InstrTmpl, {InOutInstrTmpl, InstrTmplType, ReportInstrTmpl, TaskInstrTmpl} from "@/module/instr/InstrTmpl";
import {plainToClass} from "class-transformer";
import ObjectUtil from "tools-ts/src/util/ObjectUtil";
import _ from 'lodash'

@Singleton
export default abstract class ThingModelApi {
    abstract query(productId: number): Promise<ThingModel>

    abstract queryTsl(productId: number): Promise<Tsl>

    /**
     * 物模型资源更新或添加
     * @param form
     */
    abstract resourcesUpsert(form: ResourcesUpsertForm): Promise<void>

    abstract resourceDelete(form: ResourceDeleteForm): Promise<void>

    /**
     * 物模型更新
     * @param form
     */
    abstract edit(tmodel: ThingModel): Promise<void>

    /**
     * 指令模板
     * @param id
     */
    abstract instrTmpls(id: number): Promise<Array<InstrTmpl>>
}

class ThingModelApiImpl extends BaseApi implements ThingModelApi {

    protected urlPrefix = 'thing/model'

    async query(productId: number): Promise<ThingModel> {
        const tmodel: ThingModel = await this.axios.post('query', {productId: productId})
        return this.fullThingModel(tmodel)
    }

    async queryTsl(productId: number): Promise<Tsl> {
        const tsl: Tsl = await this.axios.post('queryTsl', {productId: productId})
        return this.fullThingModel(tsl)
    }

    private fullThingModel<T extends ThingModelAware>(aware: T): T {
        const propMap: Map<string, Property> =
            ArrayUtil.groupById(aware.properties, it => it.identifier)
        aware.events.forEach(event =>
            this.fullParamProxies(propMap, event.outputParamProxies)
        )
        aware.services.forEach(service => {
            this.fullParamProxies(propMap, service.inputParamProxies)
            this.fullParamProxies(propMap, service.outputParamProxies)
        })
        return aware
    }

    private fullParamProxies(
        propMap: Map<string, Property>,
        paramProxies: Array<ParamProxy>
    ) {
        paramProxies.forEach(proxy => {
            switch (proxy.mode) {
                case ProxyMode.BIND_PROXY:
                    proxy.name = propMap.get(proxy.identifier)?.name
                        ?? cannotFindPropErr(proxy.identifier)
                    break
                case ProxyMode.STD_PROXY:
                    // ignore
                    break
                default:
                    ierror(`未处理参数代理类型: ${proxy.mode}`)
                    break
            }
        })
    }

    async resourcesUpsert(form: ResourcesUpsertForm): Promise<void> {
        const f = ObjectUtil.deepClone(form)
        this.correctResources(f.properties)
        this.correctResources(f.events)
        this.correctResources(f.services)
        f.properties.forEach(it => {
            if (_.isEmpty(it.default))
                delete it.default
        })
        return this.axios.post('resourcesUpsert', f)
    }

    private correctResources(resources: Array<Resource>) {
        resources.forEach(it => {
            if (_.isEmpty(it.description))
                delete it.description
        })
    }

    edit(tmodel: ThingModel): Promise<void> {
        return this.axios.post('edit', tmodel)
    }

    resourceDelete(form: ResourceDeleteForm): Promise<void> {
        return this.axios.post('resourceDelete', form)
    }

    async instrTmpls(productId: number): Promise<Array<InstrTmpl>> {
        const tmpls: Array<InstrTmpl> = await this.axios.post('instrTmpls', {productId: productId})
        return tmpls.map<InstrTmpl>(tmpl => {
            switch (tmpl.tmplType) {
                case InstrTmplType.IN_OUT:
                    return plainToClass(InOutInstrTmpl, tmpl)
                case InstrTmplType.REPORT:
                    return plainToClass(ReportInstrTmpl, tmpl)
                case InstrTmplType.TASK:
                    return plainToClass(TaskInstrTmpl, tmpl)
            }
        })
    }
}

Container.bind(ThingModelApi).to(ThingModelApiImpl)
